import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useTheme,
} from '@mui/material'
import { toast } from 'react-toastify'

import {
    useMoveInSurvey,
    SurveyQuestion,
    SurveyAnswer,
} from '../../hooks/useMoveInSurvey'
import { markMoveInSurveySubmitted } from '../../helpers/LocalStorage'

interface MoveInSurveyDialogProps {
    open: boolean
    leaseId: number
    onClose: () => void
    onSuccess: () => void
}

export const MoveInSurveyDialog: React.FC<MoveInSurveyDialogProps> = ({
    open,
    leaseId,
    onClose,
    onSuccess,
}) => {
    const theme = useTheme()
    const {
        isSubmitting,
        isLoadingQuestions,
        questions,
        fetchQuestions,
        submitSurvey,
    } = useMoveInSurvey()

    const [rating, setRating] = useState<number | null>(null)
    const [comment, setComment] = useState('')
    const [answers, setAnswers] = useState<Record<number, string>>({})

    useEffect(() => {
        if (open) {
            fetchQuestions(leaseId)
        }
    }, [open, leaseId, fetchQuestions])

    const setAnswer = (questionId: number, value: string) => {
        setAnswers((prev) => ({ ...prev, [questionId]: value }))
    }

    const requiredQuestionsAnswered = questions
        .filter((q) => q.required)
        .every((q) => answers[q.id]?.trim())

    const canSubmit = rating != null && requiredQuestionsAnswered && !isSubmitting

    const handleSubmit = async () => {
        if (rating == null) return
        const answerPayload: SurveyAnswer[] = Object.entries(answers)
            .filter(([, value]) => value.trim())
            .map(([questionId, value]) => ({
                question_id: Number(questionId),
                value: value.trim(),
            }))
        try {
            await submitSurvey(leaseId, rating, comment, answerPayload)
            markMoveInSurveySubmitted(leaseId)
            toast.success('Thank you for your feedback!')
            onSuccess()
        } catch {
            // Error toast handled by hook
        }
    }

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            color: '#fff',
            backgroundColor: 'rgba(255,255,255,0.04)',
            '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
            },
        },
        '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.5)' },
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    backgroundColor: '#2a2f35',
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[700]}`,
                    maxWidth: '480px',
                    width: '90%',
                },
            }}
        >
            <DialogContent sx={{ padding: theme.spacing(3) }}>
                <Typography
                    variant="h6"
                    sx={{ color: '#fff', fontWeight: 600, mb: 3 }}
                >
                    How was your move-in experience?
                </Typography>

                {/* Built-in rating */}
                <RatingSelector rating={rating} onChange={setRating} />

                {/* Built-in comment */}
                <TextField
                    multiline
                    minRows={3}
                    maxRows={6}
                    fullWidth
                    placeholder="Any additional comments?"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    helperText="Optional"
                    sx={textFieldSx}
                />

                {/* Custom questions */}
                {isLoadingQuestions && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <CircularProgress size={24} sx={{ color: theme.palette.primary.main }} />
                    </Box>
                )}

                {!isLoadingQuestions && questions.length > 0 && (
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {questions.map((q) => (
                            <CustomQuestionField
                                key={q.id}
                                question={q}
                                value={answers[q.id] || ''}
                                onChange={(val) => setAnswer(q.id, val)}
                                textFieldSx={textFieldSx}
                            />
                        ))}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ padding: theme.spacing(3), paddingTop: 0 }}>
                <Button
                    onClick={onClose}
                    sx={{
                        color: 'rgba(255,255,255,0.7)',
                        textTransform: 'none',
                    }}
                >
                    Not now
                </Button>
                <Button
                    variant="contained"
                    disabled={!canSubmit}
                    onClick={handleSubmit}
                    sx={{ textTransform: 'none', fontWeight: 600, minWidth: 90 }}
                >
                    {isSubmitting ? (
                        <CircularProgress size={20} color="inherit" />
                    ) : (
                        'Submit'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

/* ─── Rating Selector (reused for built-in + RATING questions) ─── */

interface RatingSelectorProps {
    rating: number | null
    onChange: (value: number) => void
    label?: string
}

const RatingSelector: React.FC<RatingSelectorProps> = ({ rating, onChange, label }) => {
    const theme = useTheme()
    return (
        <Box sx={{ mb: label ? 2 : 0 }}>
            {label && (
                <Typography variant="body2" sx={{ color: '#fff', mb: 1, fontWeight: 500 }}>
                    {label}
                </Typography>
            )}
            <Box
                sx={{
                    display: 'flex',
                    gap: 0.5,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    mb: 1,
                }}
            >
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                    <Box
                        key={n}
                        onClick={() => onChange(n)}
                        sx={{
                            flex: '1 1 0',
                            minWidth: 32,
                            maxWidth: 38,
                            height: 38,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 1.5,
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            color: rating === n ? '#fff' : 'rgba(255,255,255,0.7)',
                            backgroundColor:
                                rating === n
                                    ? theme.palette.primary.main
                                    : 'rgba(255,255,255,0.08)',
                            border:
                                rating === n
                                    ? `2px solid ${theme.palette.primary.main}`
                                    : '2px solid transparent',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                                backgroundColor:
                                    rating === n
                                        ? theme.palette.primary.main
                                        : 'rgba(255,255,255,0.14)',
                            },
                        }}
                    >
                        {n}
                    </Box>
                ))}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 3,
                    px: 0.5,
                }}
            >
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    Poor
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    Excellent
                </Typography>
            </Box>
        </Box>
    )
}

/* ─── Custom Question Field ─── */

interface CustomQuestionFieldProps {
    question: SurveyQuestion
    value: string
    onChange: (value: string) => void
    textFieldSx: Record<string, unknown>
}

const CustomQuestionField: React.FC<CustomQuestionFieldProps> = ({
    question,
    value,
    onChange,
    textFieldSx,
}) => {
    const theme = useTheme()
    const requiredLabel = question.required ? '' : ' (Optional)'

    if (question.question_type === 'RATING') {
        const numericValue = value ? Number(value) : null
        return (
            <Box>
                <Typography variant="body2" sx={{ color: '#fff', mb: 1, fontWeight: 500 }}>
                    {question.question_text}{requiredLabel}
                </Typography>
                <RatingSelector
                    rating={numericValue}
                    onChange={(n) => onChange(String(n))}
                />
            </Box>
        )
    }

    if (question.question_type === 'YES_NO') {
        return (
            <Box>
                <Typography variant="body2" sx={{ color: '#fff', mb: 1, fontWeight: 500 }}>
                    {question.question_text}{requiredLabel}
                </Typography>
                <ToggleButtonGroup
                    value={value || null}
                    exclusive
                    onChange={(_, newVal) => {
                        if (newVal !== null) onChange(newVal)
                    }}
                    sx={{ gap: 1 }}
                >
                    <ToggleButton
                        value="Yes"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            color: 'rgba(255,255,255,0.7)',
                            borderColor: 'rgba(255,255,255,0.2)',
                            '&.Mui-selected': {
                                backgroundColor: theme.palette.primary.main,
                                color: '#fff',
                                borderColor: theme.palette.primary.main,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                },
                            },
                        }}
                    >
                        Yes
                    </ToggleButton>
                    <ToggleButton
                        value="No"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            color: 'rgba(255,255,255,0.7)',
                            borderColor: 'rgba(255,255,255,0.2)',
                            '&.Mui-selected': {
                                backgroundColor: theme.palette.primary.main,
                                color: '#fff',
                                borderColor: theme.palette.primary.main,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                },
                            },
                        }}
                    >
                        No
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>
        )
    }

    // TEXT type
    return (
        <Box>
            <Typography variant="body2" sx={{ color: '#fff', mb: 1, fontWeight: 500 }}>
                {question.question_text}{requiredLabel}
            </Typography>
            <TextField
                multiline
                minRows={2}
                maxRows={4}
                fullWidth
                value={value}
                onChange={(e) => onChange(e.target.value)}
                sx={textFieldSx}
            />
        </Box>
    )
}
